import React, {createContext, useContext, useEffect, useMemo, useState} from 'react';
import {participantsApi} from '@/features/participants/services';

type HomeIdsState = {
  homeIds: string[];
  isLoading: boolean;
  refreshHomeIds: () => Promise<void>;
  addHomeIdIfMissing: (id: string | number | undefined | null) => void;
};

const HomeIdsContext = createContext<HomeIdsState | undefined>(undefined);

const STORAGE_KEY = 'homeIdsCache:v1';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

interface CacheShape {
  updatedAt: number;
  ids: string[];
}

export const HomeIdsProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [homeIds, setHomeIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const loadFromStorage = (): CacheShape | null => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as CacheShape;
    } catch {
      return null;
    }
  };

  const saveToStorage = (ids: string[]) => {
    try {
      const payload: CacheShape = {updatedAt: Date.now(), ids};
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
  };

  const refreshHomeIds = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return;
    }

    setIsLoading(true);
    try {
      const pageSize = 500;
      let page = 1;
      const all: string[] = [];
      while (true) {
        const params: any = {page, limit: pageSize};
        const resp = await participantsApi.list(params);
        const arr = resp.data?.participants || resp.data || [];
        if (!Array.isArray(arr) || arr.length === 0) break;
        for (const p of arr) {
          const hid = p?.home_id != null ? String(p.home_id) : undefined;
          if (hid) all.push(hid);
        }
        if (arr.length < pageSize) break;
        page++;
      }
      const unique = Array.from(new Set(all)).sort((a, b) => Number(a) - Number(b));
      setHomeIds(unique);
      saveToStorage(unique);
    } catch (e) {
      // fallback to existing cache if any
      console.error('Failed to refresh home IDs, keeping existing cache:', (e as any)?.message || e);
    } finally {
      setIsLoading(false);
    }
  };

  const addHomeIdIfMissing = (id: string | number | undefined | null) => {
    if (id == null) return;
    const hid = String(id);
    setHomeIds((prev) => {
      if (prev.includes(hid)) return prev;
      const next = [...prev, hid].sort((a, b) => Number(a) - Number(b));
      saveToStorage(next);
      return next;
    });
  };

  useEffect(() => {
    // Keep Home IDs cache available, but don't auto-fetch from participants.
    // Fetch can still be triggered explicitly via refreshHomeIds when needed.
    const cache = loadFromStorage();
    if (cache && Date.now() - cache.updatedAt < CACHE_TTL_MS) {
      setHomeIds(cache.ids);
    }
  }, []);

  const value = useMemo(
    () => ({homeIds, isLoading, refreshHomeIds, addHomeIdIfMissing}),
    [homeIds, isLoading]
  );

  return (
    <HomeIdsContext.Provider value={value}>{children}</HomeIdsContext.Provider>
  );
};

export const useHomeIds = (): HomeIdsState => {
  const ctx = useContext(HomeIdsContext);
  if (!ctx) throw new Error('useHomeIds must be used within HomeIdsProvider');
  return ctx;
};


